import { Divider, FormControlLabel, Grid, Switch } from '@mui/material';
import { GridColumn } from '@progress/kendo-react-grid';
import { Form, Formik, FormikProps } from 'formik';
import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ActionButton,
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { Checkbox } from 'src/components/shared/Checkbox/Checkbox';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { FormField } from 'src/components/shared/Forms/FormHelpers';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ModalFooter } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { MethodDto } from 'src/store/methods/method';
import { selectMethods } from 'src/store/methods/methods.selectors';
import { selectParameterGroups } from 'src/store/parametergroups/parametergroups.selectors';
import {
  ParameterDetailDto,
  ParameterMethodDto
} from 'src/store/parameters/parameter';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  parameter: Nullable<ParameterDetailDto>;
  onCancel: () => void;
  onSubmit: (parameter: ParameterDetailDto) => void;
};

export const ParameterForm = forwardRef<FormikProps<ParameterDetailDto>, Props>(
  (props, ref) => {
    const { onSubmit, onCancel } = props;

    const [showValidationErrors, setShowValidationErrors] =
      useState<boolean>(false);
    const [parameter, setParameter] = useState<ParameterDetailDto>(
      props.parameter ||
        ({
          active: true,
          corrected: false,
          mixParameter: false
        } as ParameterDetailDto)
    );
    const [methods, setMethods] = useState<MethodDto[]>([]);

    const { t } = useTranslation();

    const parameterGroups = useSelector(selectParameterGroups);
    const methods$ = useSelector(selectMethods);

    const validate = (parameter: ParameterDetailDto) => {
      const validationRunners = [
        validationRunner('name', t('props.name'), required, maxLength(50)),
        validationRunner('description', t('props.description'), maxLength(200)),
        validationRunner('decimals', t('parameter.decimals'), required),
        validationRunner(
          'parameterGroupId',
          t('parameterGroup.singular'),
          required
        )
      ];
      return runValidations(parameter, validationRunners);
    };

    useEffect(() => {
      setMethods(
        methods$.filter(
          method =>
            !parameter.parameterMethods?.some(pm => pm.methodId === method.id)
        )
      );
    }, [parameter.parameterMethods, methods$]);

    const onAddMethod = (
      parameter: ParameterDetailDto,
      method: MethodDto,
      setFieldValue: any
    ) => {
      const parameterMethods = [
        ...(parameter.parameterMethods ?? []),
        {
          parameterId: parameter.id,
          parameterName: parameter.name,
          methodId: method.id,
          methodName: method.name
        } as ParameterMethodDto
      ];
      setParameter({ ...parameter, parameterMethods: parameterMethods });
      setFieldValue('parameterMethods', parameterMethods);
    };

    const onRemoveMethod = (
      parameter: ParameterDetailDto,
      parameterMethod: ParameterMethodDto,
      setFieldValue: any
    ) => {
      const parameterMethods = parameter.parameterMethods.filter(
        pm => pm.methodId !== parameterMethod.methodId
      );
      setParameter({ ...parameter, parameterMethods: parameterMethods });
      setFieldValue('parameterMethods', parameterMethods);
    };

    return (
      <Formik
        initialValues={parameter}
        validate={validate}
        innerRef={ref}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          submitCount
        }) => (
          <Form onSubmit={handleSubmit}>
            <Grid container>
              <Grid item xs={6}>
                <InputField
                  name="name"
                  label={t('props.name')}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.name}
                  displayError={touched.name || submitCount > 0}
                />
                <InputField
                  name="description"
                  label={t('props.description')}
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.description}
                  displayError={touched.description || submitCount > 0}
                />
                <Grid container>
                  <Grid item xs={3}>
                    <InputField
                      name="decimals"
                      type="number"
                      label={t('parameter.decimals')}
                      value={values.decimals}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={errors.decimals}
                      displayError={touched.decimals || submitCount > 0}
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <FormField>
                      <Autocomplete
                        options={parameterGroups}
                        label={t('parameterGroup.singular')}
                        optionField="name"
                        value={values.parameterGroup}
                        onChange={parameterGroup =>
                          setFieldValue('parameterGroupId', parameterGroup?.id)
                        }
                        onBlur={handleBlur}
                        errorMessage={errors.parameterGroupId}
                        displayError={
                          !!errors.parameterGroupId && showValidationErrors
                        }
                      />
                    </FormField>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <InputField
                  name="nameNO"
                  label={t('language.norwegian')}
                  value={values.nameNO}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.nameNO}
                  displayError={touched.nameNO || submitCount > 0}
                />
                <InputField
                  name="nameEN"
                  label={t('language.english')}
                  value={values.nameEN}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.nameEN}
                  displayError={touched.nameEN || submitCount > 0}
                />
                <InputField
                  name="nameDE"
                  label={t('language.german')}
                  value={values.nameDE}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.nameDE}
                  displayError={touched.nameDE || submitCount > 0}
                />
              </Grid>
            </Grid>
            <FormControlLabel
              control={
                <Switch
                  checked={values.active}
                  onChange={handleChange}
                  name="active"
                />
              }
              label={t('props.active')}
            />
            <Checkbox
              label={t('parameter.corrected')}
              checked={values.corrected}
              name="corrected"
              onChange={handleChange}
            />
            <Checkbox
              label={t('parameter.mixParameter')}
              checked={values.mixParameter}
              name="mixParameter"
              onChange={handleChange}
            />

            <Divider className="mb-32" />

            <Grid container>
              <Grid item xs={6}>
                <DataGrid
                  items={values.parameterMethods}
                  style={{ height: 400 }}
                  initialSort={[{ field: 'methodName', dir: 'asc' }]}
                >
                  <GridColumn
                    field="methodName"
                    title={t('method.plural')}
                    cell={props => (
                      <td>
                        <div className="flex jc-sb align-center">
                          <BodyType truncate style={{ maxWidth: '80%' }}>
                            {props.dataItem.methodName}
                          </BodyType>
                          <ActionButton
                            icon="la la-times"
                            onClick={() =>
                              onRemoveMethod(
                                values,
                                props.dataItem,
                                setFieldValue
                              )
                            }
                          />
                        </div>
                      </td>
                    )}
                  />
                </DataGrid>
              </Grid>

              <Grid item xs={6}>
                <DataGrid
                  items={methods}
                  style={{ height: 400 }}
                  initialSort={[{ field: 'name', dir: 'asc' }]}
                >
                  <GridColumn
                    field="name"
                    title={t('method.all')}
                    cell={props => (
                      <td>
                        <div className="flex jc-sb align-center">
                          <BodyType truncate style={{ maxWidth: '80%' }}>
                            {props.dataItem.name}
                          </BodyType>
                          <ActionButton
                            icon="la la-plus"
                            onClick={() =>
                              onAddMethod(values, props.dataItem, setFieldValue)
                            }
                          />
                        </div>
                      </td>
                    )}
                  />
                </DataGrid>
              </Grid>
            </Grid>

            <ModalFooter>
              <TertiaryButton
                onClick={onCancel}
                label={t('cancel')}
                className="mr-32"
              />
              <PrimaryButton
                type="submit"
                label={t('save.verb')}
                disabled={isSubmitting}
                onClick={() => {
                  setShowValidationErrors(true);
                }}
              />
            </ModalFooter>
          </Form>
        )}
      </Formik>
    );
  }
);
